import React, { useMemo, useState } from 'react';
import { confirmDialog } from '../../lib/confirm';
import { notifyError } from '../../lib/notify';
import axios from '../../../config/axios';
import { Button, Meta, Display, Chip } from '../../components/Atoms';
import { LoadingState, ErrorState, EmptyState } from '../../components/states';
import { useMultipleApi, formatDate } from '../../lib/api';
import ScheduleShell, { ScheduleSection } from './ScheduleShell';

/**
 * ScheduleRoutine - dwie rzeczy do robienia regularnie:
 *   1. Deadheading (obrywanie przekwitłych) — grupowanie po priorytecie
 *   2. Sukcesja siewu — przypomnienia rytmiczne (np. rzodkiew co 14 dni)
 *
 * Endpointy:
 *   GET /api/plots/all-with-details
 *   GET /api/succession
 *   POST /api/succession (nowe przypomnienie)
 *   POST /api/succession/:id/complete (oznacz jako posadzone — przesuwa next_date)
 *   DELETE /api/succession/:id
 */

// Hardcoded priority lists (z legacy DeadheadingReminders.js)
const HIGH_PRIORITY = ['róża', 'petunia', 'aksamitka', 'cynia', 'lobelia', 'pelargonia', 'begonia', 'surfinia', 'wilec'];
const MEDIUM_PRIORITY = ['lawenda', 'rudbekia', 'echinacea', 'kosmos', 'georginia', 'dalia', 'astry', 'gazania', 'krwawnik'];
const LOW_PRIORITY = ['hortensja', 'floks', 'liliowiec', 'piwonia'];

function deadheadingPriority(plantName) {
  if (!plantName) return { level: 'unknown', frequency: 'opcjonalnie', benefit: '' };
  const lower = plantName.toLowerCase();
  for (const p of HIGH_PRIORITY) {
    if (lower.includes(p)) {
      return { level: 'high', frequency: '2-3× tygodniowo', benefit: 'Przedłuża kwitnienie o tygodnie, więcej pąków.' };
    }
  }
  for (const p of MEDIUM_PRIORITY) {
    if (lower.includes(p)) {
      return { level: 'medium', frequency: '1× tygodniowo', benefit: 'Stymuluje nowe kwiaty, schludny wygląd.' };
    }
  }
  for (const p of LOW_PRIORITY) {
    if (lower.includes(p)) {
      return { level: 'low', frequency: 'opcjonalnie', benefit: 'Bardziej dla estetyki niż przedłużenia kwitnienia.' };
    }
  }
  return { level: 'unknown', frequency: 'opcjonalnie', benefit: '' };
}

function isInBloom(bloomSeason, currentMonth) {
  if (!bloomSeason) return false;
  const cleaned = bloomSeason.replace(/\(.*?\)/g, '').trim().toLowerCase();
  const monthNames = {
    'styczeń': 1, 'luty': 2, 'marzec': 3, 'kwiecień': 4, 'maj': 5,
    'czerwiec': 6, 'lipiec': 7, 'sierpień': 8, 'wrzesień': 9,
    'październik': 10, 'listopad': 11, 'grudzień': 12,
  };
  const seasons = { 'wiosna': [3,4,5], 'lato': [6,7,8], 'jesień': [9,10,11], 'zima': [12,1,2] };
  for (const [s, m] of Object.entries(seasons)) {
    if (cleaned.includes(s)) return m.includes(currentMonth);
  }
  if (cleaned.includes('-')) {
    const [a, b] = cleaned.split('-').map(s => s.trim());
    const aN = monthNames[a], bN = monthNames[b];
    if (aN && bN) {
      let m = aN;
      while (true) {
        if (m === currentMonth) return true;
        if (m === bN) break;
        m = m === 12 ? 1 : m + 1;
      }
      return false;
    }
  }
  for (const [name, n] of Object.entries(monthNames)) {
    if (cleaned === name || cleaned.startsWith(name + ' ')) return n === currentMonth;
  }
  return false;
}

function DeadheadingCard({ plant, blooming }) {
  const { level, frequency, benefit } = deadheadingPriority(plant.plant_name);
  const tone = level === 'high' ? 'rust' : level === 'medium' ? 'amber' : level === 'low' ? 'sage' : 'dim';
  const label = level === 'high' ? 'Wysoki priorytet' :
                level === 'medium' ? 'Średni' :
                level === 'low' ? 'Niski' : 'Nieokreślony';
  return (
    <div style={{
      padding: '14px 16px', borderRadius: 10,
      background: 'var(--t-bg-elev)',
      border: '1px solid var(--t-line)',
      borderLeft: `3px solid var(--t-${tone === 'dim' ? 'line-2' : tone})`,
      opacity: blooming ? 1 : 0.7,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
        <Display size={15} style={{ display: 'inline', letterSpacing: '-.01em' }}>
          {plant.plant_name}
        </Display>
        <Chip tone={tone}>{label}</Chip>
        {blooming && <Chip tone="sage">🌸 Kwitnie teraz</Chip>}
      </div>
      <div style={{ fontSize: 11, color: 'var(--t-text-mute)', marginBottom: 8 }}>
        {plant.plot_name} · Rząd {plant.row_number}
      </div>
      <div style={{ fontSize: 12, color: 'var(--t-text-dim)', lineHeight: 1.45 }}>
        <strong style={{ color: 'var(--t-text)' }}>Częstotliwość:</strong> {frequency}
      </div>
      {benefit && (
        <div style={{ fontSize: 12, color: 'var(--t-text-mute)', marginTop: 4, fontStyle: 'italic' }}>
          {benefit}
        </div>
      )}
    </div>
  );
}

function SuccessionAddForm({ onAdded }) {
  const [name, setName] = useState('');
  const [interval, setInterval] = useState(14);
  const [lastDate, setLastDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handle = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) {
      setErr('Podaj nazwę rośliny.');
      return;
    }
    setSaving(true);
    setErr('');
    try {
      await axios.post('/api/succession', {
        plant_name: name.trim(),
        interval_days: parseInt(interval, 10),
        last_planted_date: lastDate,
      });
      setName('');
      setInterval(14);
      onAdded?.();
    } catch (er) {
      setErr(er.response?.data?.error || 'Nie udało się zapisać.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handle} style={{
      padding: '16px 18px', borderRadius: 12,
      background: 'var(--t-bg-elev)',
      border: '1px solid var(--t-line)',
      marginBottom: 18,
    }}>
      <Meta style={{ marginBottom: 10 }}>Nowe przypomnienie</Meta>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr 1fr auto',
        gap: 10, alignItems: 'flex-end',
      }}>
        <div>
          <label style={{
            display: 'block', marginBottom: 4,
            fontSize: 11, color: 'var(--t-text-dim)',
          }}>Roślina</label>
          <input
            type="text"
            aria-label="Roślina"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="np. rzodkiew"
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8,
              background: 'var(--t-bg)', border: '1px solid var(--t-line-2)',
              color: 'var(--t-text)', fontSize: 14,
              fontFamily: 'var(--t-sans)',
            }}
          />
        </div>
        <div>
          <label style={{
            display: 'block', marginBottom: 4,
            fontSize: 11, color: 'var(--t-text-dim)',
          }}>Co X dni</label>
          <input
            type="number"
            aria-label="Co ile dni"
            min={1}
            max={365}
            value={interval}
            onChange={e => setInterval(e.target.value)}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8,
              background: 'var(--t-bg)', border: '1px solid var(--t-line-2)',
              color: 'var(--t-text)', fontSize: 14,
              fontFamily: 'var(--t-mono)', textAlign: 'right',
            }}
          />
        </div>
        <div>
          <label style={{
            display: 'block', marginBottom: 4,
            fontSize: 11, color: 'var(--t-text-dim)',
          }}>Ostatnio posiane</label>
          <input
            type="date"
            aria-label="Ostatnio posiane"
            value={lastDate}
            onChange={e => setLastDate(e.target.value)}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8,
              background: 'var(--t-bg)', border: '1px solid var(--t-line-2)',
              color: 'var(--t-text)', fontSize: 14,
              colorScheme: 'dark',
            }}
          />
        </div>
        <Button type="submit" variant="primary" disabled={saving}>
          {saving ? '…' : '+ Dodaj'}
        </Button>
      </div>
      {err && (
        <div style={{ marginTop: 10, fontSize: 12, color: 'var(--t-rust)' }}>{err}</div>
      )}
    </form>
  );
}

function SuccessionRow({ item, today, onComplete, onDelete }) {
  const isOverdue = item.next_planting_date && item.next_planting_date < today;
  const daysToNext = item.next_planting_date
    ? Math.round((new Date(item.next_planting_date) - new Date(today)) / 86400000)
    : null;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '12px 14px', borderRadius: 10,
      background: 'var(--t-bg-elev)',
      border: '1px solid var(--t-line)',
      borderLeft: `3px solid var(--t-${isOverdue ? 'rust' : 'sage'})`,
      marginBottom: 8,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, color: 'var(--t-text)', marginBottom: 4 }}>
          {item.plant_name}
        </div>
        <div style={{ fontSize: 11, color: 'var(--t-text-mute)' }}>
          Co {item.interval_days} dni · ostatnio {formatDate(item.last_planted_date)}
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        {item.next_planting_date && (
          <div style={{
            fontSize: 12,
            color: isOverdue ? 'var(--t-rust)' :
                   daysToNext <= 3 ? 'var(--t-amber)' : 'var(--t-text-dim)',
          }}>
            {isOverdue
              ? `Spóźnione o ${Math.abs(daysToNext)}d`
              : daysToNext === 0
              ? 'Dziś'
              : `Za ${daysToNext}d`}
          </div>
        )}
        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
          <button
            type="button"
            onClick={() => onComplete(item.id)}
            style={{
              padding: '4px 10px', borderRadius: 6,
              background: 'transparent', border: '1px solid var(--t-sage)',
              color: 'var(--t-sage)', fontSize: 11, cursor: 'pointer',
              fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
            }}
            title="Posadziłem - przesuń termin"
          >POSIANO</button>
          <button
            type="button"
            onClick={() => onDelete(item.id)}
            style={{
              padding: '4px 10px', borderRadius: 6,
              background: 'transparent', border: '1px solid var(--t-line-2)',
              color: 'var(--t-text-mute)', fontSize: 11, cursor: 'pointer',
            }}
            title="Usuń"
          >×</button>
        </div>
      </div>
    </div>
  );
}

export default function ScheduleRoutine() {
  const [tab, setTab] = useState('deadheading'); // 'deadheading' | 'succession'
  const { results, loading, errors, refetch } = useMultipleApi({
    plots: '/api/plots/all-with-details',
    succession: '/api/succession',
  });

  const flowers = useMemo(() => {
    if (!results.plots) return [];
    const out = [];
    results.plots.forEach(p => {
      (p.beds || []).forEach(b => {
        if (b.plant_category?.startsWith('flower_')) {
          out.push({ ...b, plot_name: p.name });
        }
      });
    });
    return out;
  }, [results.plots]);

  const today = new Date().toISOString().split('T')[0];
  const currentMonth = new Date().getMonth() + 1;

  const grouped = useMemo(() => {
    const groups = { high: [], medium: [], low: [], unknown: [] };
    flowers.forEach(f => {
      const { level } = deadheadingPriority(f.plant_name);
      groups[level].push({ ...f, blooming: isInBloom(f.bloom_season, currentMonth) });
    });
    return groups;
  }, [flowers, currentMonth]);

  const error = errors.plots || errors.succession;
  if (error?.includes('autoryz') || error === 'Brak tokenu autoryzacji') {
    return <ScheduleShell><ErrorState error={{ status: 401, message: error }} /></ScheduleShell>;
  }
  if (loading) return <ScheduleShell><LoadingState /></ScheduleShell>;

  const succession = results.succession || [];
  const succActive = succession.filter(s => s.is_active);
  const succOverdue = succActive.filter(s => s.next_planting_date && s.next_planting_date < today);
  const succUpcoming = succActive.filter(s => s.next_planting_date && s.next_planting_date >= today);

  const handleComplete = async (id) => {
    try {
      await axios.post(`/api/succession/${id}/complete`);
      refetch();
    } catch (err) {
      notifyError('Nie udało się oznaczyć: ' + (err.response?.data?.error || ''));
    }
  };

  const handleDelete = async (id) => {
    if (!await confirmDialog('Usunąć przypomnienie?')) return;
    try {
      await axios.delete(`/api/succession/${id}`);
      refetch();
    } catch (err) {
      notifyError('Nie udało się usunąć: ' + (err.response?.data?.error || ''));
    }
  };

  const totalDeadheading = grouped.high.length + grouped.medium.length;

  return (
    <ScheduleShell
      eyebrow="Pielęgnacja bieżąca"
      title="Codzienne i tygodniowe rytuały"
      subtitle="Obrywanie przekwitłych pąków, regularne sianie. Małe rzeczy, które robisz cyklicznie i zapominasz, że robisz."
    >
      {/* Tab switch */}
      <div style={{
        display: 'flex', gap: 8, padding: 4,
        background: 'var(--t-bg-elev)', borderRadius: 10,
        border: '1px solid var(--t-line)',
        marginBottom: 28, width: 'fit-content',
      }}>
        {[
          { k: 'deadheading', label: `Deadheading (${totalDeadheading})` },
          { k: 'succession',  label: `Sukcesja siewu (${succActive.length})` },
        ].map(opt => (
          <button
            key={opt.k}
            type="button"
            onClick={() => setTab(opt.k)}
            style={{
              padding: '8px 14px', borderRadius: 8,
              background: tab === opt.k ? 'var(--t-bg)' : 'transparent',
              border: 'none', cursor: 'pointer',
              color: tab === opt.k ? 'var(--t-text)' : 'var(--t-text-dim)',
              fontFamily: 'var(--t-sans)', fontSize: 13,
              fontWeight: tab === opt.k ? 500 : 400,
            }}
          >{opt.label}</button>
        ))}
      </div>

      {tab === 'deadheading' && (
        <>
          <div style={{
            padding: '14px 16px', borderRadius: 10,
            background: 'rgba(232, 168, 184, 0.06)',
            borderLeft: '3px solid #e8a8b8',
            marginBottom: 24, fontSize: 13, color: 'var(--t-text-dim)', lineHeight: 1.5,
          }}>
            <strong style={{ color: 'var(--t-text)' }}>Deadheading</strong> — usuwanie przekwitniętych kwiatów. Zmusza roślinę do produkcji kolejnych pąków zamiast nasion. Im częściej, tym dłużej kwitnie.
          </div>

          {flowers.length === 0 ? (
            <EmptyState
              title="Brak kwiatów"
              description="Dodaj kwiaty do swoich grządek — pojawią się tutaj z konkretnymi wskazówkami obrywania."
            />
          ) : (
            <>
              {['high', 'medium', 'low', 'unknown'].map(level => {
                const list = grouped[level];
                if (list.length === 0) return null;
                const sectionLabel = level === 'high' ? 'Wysoki priorytet (2-3× tygodniowo)' :
                                     level === 'medium' ? 'Średni priorytet (1× tygodniowo)' :
                                     level === 'low' ? 'Niski priorytet (opcjonalnie)' :
                                     'Nieokreślony priorytet';
                return (
                  <ScheduleSection key={level} title={sectionLabel}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                      gap: 10,
                    }}>
                      {list.map(p => (
                        <DeadheadingCard key={p.id} plant={p} blooming={p.blooming} />
                      ))}
                    </div>
                  </ScheduleSection>
                );
              })}
            </>
          )}
        </>
      )}

      {tab === 'succession' && (
        <>
          <div style={{
            padding: '14px 16px', borderRadius: 10,
            background: 'rgba(168, 196, 158, 0.06)',
            borderLeft: '3px solid var(--t-sage)',
            marginBottom: 24, fontSize: 13, color: 'var(--t-text-dim)', lineHeight: 1.5,
          }}>
            <strong style={{ color: 'var(--t-text)' }}>Sukcesja siewu</strong> — sadzenie tej samej rośliny w odstępach (np. rzodkiew co 14 dni), żeby mieć ciągły zbiór zamiast jednego dużego.
          </div>

          {succOverdue.length > 0 && (
            <div style={{
              padding: '14px 16px', borderRadius: 10,
              background: 'rgba(192, 106, 74, 0.08)',
              borderLeft: '3px solid var(--t-rust)',
              marginBottom: 18, fontSize: 13, color: 'var(--t-text)',
            }}>
              ⚠ <strong>{succOverdue.length}</strong> {succOverdue.length === 1 ? 'siew się' : 'siewów się'} spóźniło.
            </div>
          )}

          <SuccessionAddForm onAdded={refetch} />

          {succActive.length === 0 ? (
            <EmptyState
              title="Brak przypomnień"
              description="Dodaj pierwsze przypomnienie nad formularzem — np. rzodkiew co 14 dni."
            />
          ) : (
            <>
              {succOverdue.length > 0 && (
                <ScheduleSection title="Spóźnione">
                  {succOverdue.map(s => (
                    <SuccessionRow key={s.id} item={s} today={today}
                      onComplete={handleComplete} onDelete={handleDelete} />
                  ))}
                </ScheduleSection>
              )}
              <ScheduleSection title={`Najbliższe (${succUpcoming.length})`}>
                {succUpcoming.length > 0 ? (
                  succUpcoming.map(s => (
                    <SuccessionRow key={s.id} item={s} today={today}
                      onComplete={handleComplete} onDelete={handleDelete} />
                  ))
                ) : (
                  <Meta>Wszystkie aktywne przypomnienia mają nadchodzące daty w nieokreślonej przyszłości.</Meta>
                )}
              </ScheduleSection>
            </>
          )}
        </>
      )}
    </ScheduleShell>
  );
}
